import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ReplayIcon from "@mui/icons-material/Replay";
import baseUrl from "../../../../../api/baseUrl";
import { getCurrencySymbol } from "../../../../../utils/currency/currencyUtils";
import SelectedBillPopUp from "../../../../../branch/pages/sales/invoice/SelectedBillPopUp";
import AdminAuthModal from "./AdminAuthModal";

type SalesReturnSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const formatDateTime = (value: any) => {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

function SalesReturnSearchModal({
  isOpen,
  onClose,
}: SalesReturnSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [bills, setBills] = useState<any[]>([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedBill, setSelectedBill] = useState<any>(null);
  const [pendingBill, setPendingBill] = useState<any>(null);
  const [isAdminAuthOpen, setIsAdminAuthOpen] = useState(false);

  const currencySymbol = getCurrencySymbol();

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setBills([]);
      setErrorMessage("");
      return;
    }

    let isActive = true;

    const fetchBills = async () => {
      setLoadingBills(true);
      setErrorMessage("");

      try {
        const response = await baseUrl.get("/branch/pos/get-total-sales");
        const loadedBills = Array.isArray(response.data?.data)
          ? [...response.data.data]
          : [];

        loadedBills.sort((left: any, right: any) => {
          const leftTime = new Date(left.createdAt || 0).getTime();
          const rightTime = new Date(right.createdAt || 0).getTime();
          return rightTime - leftTime;
        });

        if (isActive) {
          setBills(loadedBills);
        }
      } catch (error) {
        console.error("Error loading POS bills:", error);
        if (isActive) {
          setBills([]);
          setErrorMessage("Unable to load sales bills. Please try again.");
        }
      } finally {
        if (isActive) {
          setLoadingBills(false);
        }
      }
    };

    fetchBills();

    return () => {
      isActive = false;
    };
  }, [isOpen]);

  const filteredBills = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return bills.slice(0, 25);
    }

    return bills
      .filter((bill: any) => {
        const billNumber = String(bill.billNumber || "").toLowerCase();
        const customerName = String(
          bill.customerName || bill.Customer?.name || bill.customer?.name || "",
        ).toLowerCase();
        return billNumber.includes(query) || customerName.includes(query);
      })
      .slice(0, 50);
  }, [bills, searchQuery]);

  const handleOpenBill = async (bill: any) => {
    setLoadingBillDetails(true);
    setErrorMessage("");

    try {
      const response = await baseUrl.get(
        `/branch/sale/sale-details/${bill.billNumber}`,
      );
      const saleDetails = response.data?.data || {};

      setSelectedBill({
        billNumber: bill.billNumber,
        ...saleDetails,
        payments: {
          ...(saleDetails.payments || {}),
          isCreditSale: Boolean(saleDetails.payments?.isCreditSale),
          unpaidAmount: Number(
            saleDetails.payments?.unpaidAmount ||
              saleDetails.payments?.creditAmount ||
              0,
          ),
          creditAmount: Number(
            saleDetails.payments?.creditAmount ||
              saleDetails.payments?.unpaidAmount ||
              0,
          ),
        },
      });
    } catch (error) {
      console.error("Error loading selected sale bill:", error);
      setErrorMessage("Unable to open this bill. Please try again.");
    } finally {
      setLoadingBillDetails(false);
    }
  };

  const requestOpenBill = (bill: any) => {
    setPendingBill(bill);
    setIsAdminAuthOpen(true);
  };

  const handleAdminVerified = () => {
    setIsAdminAuthOpen(false);
    if (pendingBill) {
      handleOpenBill(pendingBill);
    }
    setPendingBill(null);
  };

  const handleAdminAuthCancel = () => {
    setIsAdminAuthOpen(false);
    setPendingBill(null);
  };

  const handleCloseSelectedBill = () => {
    setSelectedBill(null);
  };

  const handleCloseLookup = () => {
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseLookup();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-erp-navy/55 p-3 sm:p-5 backdrop-blur-[3px]"
          onClick={handleOverlayClick}
        >
          <div
            className="flex h-[min(88vh,820px)] w-full max-w-[1000px] flex-col overflow-hidden rounded-2xl border border-erp-line bg-white font-inter shadow-[0_24px_60px_rgba(11,28,44,0.28)]"
            role="dialog"
            aria-modal="true"
            aria-label="Sales Return Lookup"
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-erp-gold/40 bg-erp-gold/20 text-erp-gold">
                  <ReplayIcon className="!text-[20px]" />
                </div>
                <div className="min-w-0">
                  <h2 className="m-0 text-[16px] font-bold tracking-wide text-white">
                    Sales Return Lookup
                  </h2>
                  <p className="m-0 mt-0.5 text-[11px] font-medium text-white/60">
                    Find a bill and process a return
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseLookup}
                className="inline-flex rounded-lg border border-white/15 bg-white/10 p-1.5 text-white/85 transition-colors hover:bg-white/20 hover:text-white"
              >
                <CloseIcon className="!text-[18px]" />
              </button>
            </div>

            {/* Body */}
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4 sm:p-5">
              <p className="m-0 shrink-0 text-[12px] leading-relaxed text-erp-muted">
                Search a bill number, open it, then use the bill dialog to edit
                and save the return just like in the dashboard invoice flow.
              </p>

              <div className="relative shrink-0">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 z-[1] !-translate-y-1/2 !text-[18px] text-erp-muted" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.preventDefault();
                  }}
                  placeholder="Type bill number or customer name…"
                  className="h-11 w-full rounded-[10px] border border-erp-line bg-erp-surface pl-10 pr-3 text-[13px] font-medium text-erp-ink outline-none transition-[border-color,box-shadow,background] placeholder:text-[#8ea3b8] focus:border-erp-accent focus:bg-white focus:shadow-[0_0_0_3px_rgba(31,111,139,0.14)]"
                />
              </div>

              {errorMessage ? (
                <div className="shrink-0 rounded-lg border border-erp-danger/25 bg-erp-danger/10 px-3 py-2.5 text-[13px] font-semibold text-erp-danger">
                  {errorMessage}
                </div>
              ) : null}

              <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-erp-line bg-white [scrollbar-width:thin]">
                {loadingBills ? (
                  <div className="flex h-full min-h-[240px] items-center justify-center text-[13px] font-semibold text-erp-muted">
                    Loading sales bills...
                  </div>
                ) : (
                  <table className="w-full min-w-[760px] border-collapse text-left">
                    <thead className="sticky top-0 z-[1]">
                      <tr className="border-b border-erp-line bg-erp-surface text-[11px] font-bold uppercase tracking-[0.04em] text-erp-muted">
                        <th className="px-3 py-2.5 font-bold">Bill No</th>
                        <th className="px-3 py-2.5 font-bold">Date</th>
                        <th className="px-3 py-2.5 font-bold">Customer</th>
                        <th className="px-3 py-2.5 text-right font-bold">
                          Net Total
                        </th>
                        <th className="px-3 py-2.5 text-right font-bold">Paid</th>
                        <th className="px-3 py-2.5 text-center font-bold">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBills.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-3 py-10 text-center text-[13px] font-semibold text-erp-muted"
                          >
                            No bills found.
                          </td>
                        </tr>
                      ) : (
                        filteredBills.map((bill: any) => (
                          <tr
                            key={bill.id}
                            className="border-b border-erp-line transition-colors last:border-b-0 hover:bg-erp-surface/80"
                          >
                            <td className="px-3 py-3 text-[13px] font-bold text-erp-ink">
                              {bill.billNumber || "-"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-[12px] font-medium text-erp-muted">
                              {formatDateTime(bill.createdAt)}
                            </td>
                            <td className="max-w-[200px] truncate px-3 py-3 text-[13px] font-semibold text-erp-ink">
                              {bill.customerName ||
                                bill.Customer?.name ||
                                bill.customer?.name ||
                                (bill.customerId
                                  ? "Customer"
                                  : "Walk-In Customer")}
                            </td>
                            <td className="px-3 py-3 text-right text-[13px] font-semibold tabular-nums text-erp-ink">
                              {currencySymbol}{" "}
                              {Number(bill.netTotal || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-3 text-right text-[13px] font-semibold tabular-nums text-erp-ink">
                              {currencySymbol}{" "}
                              {Number(bill.amountRecived || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => requestOpenBill(bill)}
                                disabled={loadingBillDetails}
                                className="inline-flex h-8 min-w-[108px] items-center justify-center gap-1 rounded-lg bg-erp-navy px-3 text-[11px] font-bold uppercase tracking-[0.04em] text-white transition-colors hover:bg-erp-navy-mid disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <ReplayIcon className="!text-[14px]" />
                                Open Bill
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 justify-end border-t border-erp-line bg-white px-5 py-3.5">
              <button
                type="button"
                onClick={handleCloseLookup}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-erp-line bg-white px-4 text-[12px] font-bold text-erp-muted transition-colors hover:border-erp-muted hover:text-erp-ink"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <SelectedBillPopUp
        selectedBill={selectedBill}
        openModal={Boolean(selectedBill)}
        handleCloseModal={handleCloseSelectedBill}
      />

      <AdminAuthModal
        isOpen={isAdminAuthOpen}
        onCancel={handleAdminAuthCancel}
        onVerified={handleAdminVerified}
      />
    </>
  );
}

export default SalesReturnSearchModal;
